import React from 'react';

// Marketplace "groups": the product areas a MyTools item gets added to on install. The creator picks
// them in the Create wizard; the API stores them (my_tool_groups), validates them and routes installs
// by them. This catalog mirrors MomentumAMPAPI/Utility/MarketplaceGroups.cs — keep the two in step.

export type GroupToolType = 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';

/** route = install really sends it there · label = true, but install takes no action · soon = no hook yet */
export type GroupStatus = 'route' | 'label' | 'soon';

export interface GroupDef {
    key: string;
    label: string;
    status: GroupStatus;
    /** Always on (an agent install IS a Digital Workforce agent). */
    locked?: boolean;
    /** One-line explanation shown in the wizard. */
    hint: string;
}

const DW = 'digital_workforce';
const TOOLBOX = 'toolbox';
export const GROUP_KEYS = { DW, TOOLBOX } as const;

/** Widget placement groups ↔ manifest surfaces. The manifest owns these; they are never stored as groups. */
export const SURFACE_TO_GROUP: Record<string, string> = { page: 'widgets_page', sidebar: 'sidebar', tile: 'home_tile' };
export const GROUP_TO_SURFACE: Record<string, string> = { widgets_page: 'page', sidebar: 'sidebar', home_tile: 'tile' };
export const isSurfaceGroup = (key: string) => key in GROUP_TO_SURFACE;

const LABELS: Record<string, string> = {
    digital_workforce: 'Digital Workforce',
    toolbox: 'Toolbox',
    widgets_page: 'Widgets page',
    sidebar: 'Right sidebar',
    home_tile: 'Home tile',
};

export const groupLabel = (key: string) => LABELS[key] ?? key;

// Skills, agents and MCP servers always land in Digital Workforce and nowhere else, so only
// widgets have a choice to make (the wizard shows the picker for widgets only).
export const GROUP_CATALOG: Record<GroupToolType, GroupDef[]> = {
    skill: [
        { key: DW, label: LABELS[DW], status: 'route', locked: true, hint: "Added to each installer's library in Digital Workforce, to turn on for any agent." },
    ],
    agent: [
        { key: DW, label: LABELS[DW], status: 'route', locked: true, hint: 'Installing creates the agent in Digital Workforce.' },
    ],
    mcp_server: [
        { key: DW, label: LABELS[DW], status: 'route', locked: true, hint: "Added to each installer's library in Digital Workforce, to turn on for any agent." },
    ],
    widget: [
        { key: 'widgets_page', label: LABELS.widgets_page, status: 'route', hint: 'Shows on the Widgets page.' },
        { key: 'sidebar', label: LABELS.sidebar, status: 'route', hint: 'Shows in the right sidebar panel.' },
        { key: 'home_tile', label: LABELS.home_tile, status: 'route', hint: 'Shows as a tile on the home screen.' },
        { key: TOOLBOX, label: LABELS[TOOLBOX], status: 'route', hint: 'Listed in Toolbox once approved as Global.' },
    ],
    // Integrations install through their own flow; workflows aren't publishable yet.
    workflow: [],
    integration: [],
};

/** Stored (non-surface) groups a new item starts with — what each type did before groups existed. */
export function defaultGroups(type: GroupToolType): string[] {
    switch (type) {
        case 'skill':
        case 'agent':
        case 'mcp_server':
            return [DW];
        case 'widget':
            return [TOOLBOX];
        default:
            return [];
    }
}

/** Same rules the API enforces; returns a message, or '' when the selection is valid. */
export function validateGroups(type: GroupToolType, groups: string[], widgetSurfaces?: string[]): string {
    if (type === 'widget' && widgetSurfaces && widgetSurfaces.length === 0)
        return 'Pick at least one place for the widget to show.';
    return '';
}

/** The groups to send on save: catalog order, locked keys added, surface groups dropped. */
export function groupsForSave(type: GroupToolType, groups: string[]): string[] {
    const defs = GROUP_CATALOG[type] ?? [];
    return defs
        .filter(d => d.status !== 'soon' && !isSurfaceGroup(d.key) && (d.locked || groups.includes(d.key)))
        .map(d => d.key);
}

const statusOf = (type: GroupToolType | undefined, key: string): GroupStatus =>
    (type && GROUP_CATALOG[type]?.find(d => d.key === key)?.status) || 'route';

// ── Filter helpers (same shape as publisherFilter.tsx) ──

export const ALL_GROUPS = '';

/** Distinct groups across a list, in a stable, readable order. */
export function groupOptions(lists: Array<string[] | undefined>): string[] {
    const order = Object.keys(LABELS);
    const seen = new Set<string>();
    lists.forEach(l => (l ?? []).forEach(g => seen.add(g)));
    return Array.from(seen).sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
}

export function matchesGroup(groups: string[] | undefined, selected: string): boolean {
    return !selected || (groups ?? []).includes(selected);
}

export const GroupFilterSelect: React.FC<{
    value: string;
    options: string[];
    onChange: (value: string) => void;
    className?: string;
}> = ({ value, options, onChange, className }) => {
    if (options.length < 2 && !value)
        return null;
    const list = value && !options.includes(value) ? [value, ...options] : options;
    return (<select className={className} value={value} onChange={e => onChange(e.target.value)} aria-label="Filter by group">
      <option value={ALL_GROUPS}>All Groups</option>
      {list.map(g => (<option key={g} value={g}>{groupLabel(g)}</option>))}
    </select>);
};

/** Chips for an item's groups: route = accent, label = outlined, soon = muted. */
export const GroupChips: React.FC<{
    groups: string[] | undefined;
    type?: GroupToolType;
    className?: string;
}> = ({ groups, type, className }) => {
    // Only a widget has places to show: everything else always lands in Digital Workforce,
    // so a "Digital Workforce" chip on it says nothing.
    if (!groups || groups.length === 0 || type !== 'widget')
        return null;
    return (<div className={`mt-group-chips ${className ?? ''}`} aria-label="Added to">
      {groups.map(g => {
            const status = statusOf(type, g);
            return (<span key={g} className={`mt-group-chip mt-group-chip--${status}`} title={status === 'label' ? `${groupLabel(g)} (works with, nothing installed there)` : `Added to ${groupLabel(g)}`}>
            {groupLabel(g)}
          </span>);
        })}
    </div>);
};

/**
 * Wizard picker: one checkbox per group the type allows. Widget placement checkboxes edit the
 * widget's surfaces (saved with the bundle upload); everything else edits the stored groups.
 */
export const GroupPicker: React.FC<{
    type: GroupToolType;
    groups: string[];
    setGroups: (g: string[]) => void;
    widgetSurfaces?: string[];
    setWidgetSurfaces?: (s: string[]) => void;
}> = ({ type, groups, setGroups, widgetSurfaces, setWidgetSurfaces }) => {
    const defs = GROUP_CATALOG[type] ?? [];
    if (defs.length === 0)
        return null;
    const isChecked = (d: GroupDef) => d.locked
        || (isSurfaceGroup(d.key) ? (widgetSurfaces ?? []).includes(GROUP_TO_SURFACE[d.key]) : groups.includes(d.key));
    const toggle = (d: GroupDef) => {
        if (d.locked || d.status === 'soon')
            return;
        if (isSurfaceGroup(d.key)) {
            const s = GROUP_TO_SURFACE[d.key];
            const cur = widgetSurfaces ?? [];
            setWidgetSurfaces?.(cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);
            return;
        }
        setGroups(groups.includes(d.key) ? groups.filter(g => g !== d.key) : [...groups, d.key]);
    };
    return (<div className="mt-group-picker">
      {defs.map(d => (<label key={d.key} className={`mt-group-option ${d.status === 'soon' ? 'mt-group-option--soon' : ''} ${isChecked(d) && d.status !== 'soon' ? 'mt-group-option--on' : ''}`}>
          <input type="checkbox" checked={d.status !== 'soon' && isChecked(d)} disabled={d.locked || d.status === 'soon'} onChange={() => toggle(d)}/>
          <span className="mt-group-option__text">
            <span className="mt-group-option__label">
              {d.label}
              {d.status === 'label' && <span className="mt-group-option__tag">Works with</span>}
              {d.status === 'soon' && <span className="mt-group-option__tag">Coming soon</span>}
              {d.locked && <span className="mt-group-option__tag">Always</span>}
            </span>
            <span className="mt-group-option__hint">{d.hint}</span>
          </span>
        </label>))}
    </div>);
};
